// Each person's colour (user_permissions.user_color), picked in Admin › Users
// from USER_COLORS. One colour, used two ways:
//   - solid behind their initials on task bubbles — every one carries white
//     text at 4.5:1 or better;
//   - as a light tint (tint(), ~84% white) behind their notes on orders, so
//     the black note text sits at about 14:1.
// Colours set before the picker existed (light pastels, the #f8f9fa default)
// still work: notes show them as they are, bubbles fall back to a palette
// colour until a real one is picked.

export const USER_COLORS: { value: string; name: string }[] = [
  { value: '#3471CC', name: 'Blue' },
  { value: '#0E7C86', name: 'Teal' },
  { value: '#2E7D52', name: 'Green' },
  { value: '#A85B17', name: 'Amber' },
  { value: '#C0392B', name: 'Red' },
  { value: '#A0527A', name: 'Plum' },
  { value: '#7B4FBF', name: 'Purple' },
  { value: '#5F6B7A', name: 'Slate' },
];

export const DEFAULT_USER_COLOR = '#f8f9fa';

function parseHex(hex: string | null | undefined): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function luminance(hex: string | null | undefined): number | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(rgb[0]) + 0.7152 * channel(rgb[1]) + 0.0722 * channel(rgb[2]);
}

/** True for a colour strong enough to sit behind white initials. */
export function isSolidColor(hex: string | null | undefined): boolean {
  const lum = luminance(hex);
  return lum !== null && lum < 0.55;
}

/** Mixes a colour toward white; amount 0..1 is how much white. */
export function tint(hex: string, amount = 0.84): string {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const mix = (v: number) => Math.round(v + (255 - v) * amount);
  return `#${rgb.map((v) => mix(v).toString(16).padStart(2, '0')).join('')}`;
}

/** Background for someone's notes on an order. */
export function noteColor(hex: string | null | undefined): string {
  if (!hex) return DEFAULT_USER_COLOR;
  return isSolidColor(hex) ? tint(hex) : hex;
}
